import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import { SystemMessageDtoIn } from '@/models/in/SystemMessageDtoIn';
import { useAnnouncements } from './AnnouncementsContext';

/** How often the tour looks for its element. Anchors appear with data and dialogs come and go. */
const LOOK_INTERVAL_MS = 700;

interface Target {
  message: SystemMessageDtoIn;
  stepIndex: number;
  element: HTMLElement;
  /** The next step's element is on this screen now, so «הבא» has somewhere to go. */
  nextHere: boolean;
}

/** The step a reader is on, kept per browser: finishing a bubble is recorded on the server, getting halfway is not worth a column. */
const progressKey = (email: string | undefined, messageId: number) => `announcement.tour.${email ?? ''}.${messageId}`;

const readProgress = (email: string | undefined, messageId: number): number => {
  try {
    const stored = Number(localStorage.getItem(progressKey(email, messageId)));
    return Number.isFinite(stored) && stored > 0 ? stored : 0;
  } catch {
    return 0;
  }
};

const writeProgress = (email: string | undefined, messageId: number, step: number | null) => {
  try {
    if (step === null) localStorage.removeItem(progressKey(email, messageId));
    else localStorage.setItem(progressKey(email, messageId), String(step));
  } catch {
    // A private window starts the bubble from its first step next time; nothing worse.
  }
};

const isShown = (element: HTMLElement) => element.isConnected && element.getClientRects().length > 0;

/**
 * At least part of the element is on screen: inside the window and not scrolled away inside a dialog
 * or a panel. A bubble beside an element the user cannot see would point at nothing, so a step below
 * the fold waits until the user gets there.
 */
const isInView = (element: HTMLElement) => {
  const rect = element.getBoundingClientRect();
  if (rect.bottom <= 0 || rect.top >= window.innerHeight || rect.right <= 0 || rect.left >= window.innerWidth) return false;
  for (let parent = element.parentElement; parent && parent !== document.body; parent = parent.parentElement) {
    const { overflowX, overflowY } = getComputedStyle(parent);
    if (!/auto|scroll|hidden/.test(overflowX + overflowY)) continue;
    const box = parent.getBoundingClientRect();
    if (rect.bottom <= box.top || rect.top >= box.bottom) return false;
  }
  return true;
};

/**
 * Something is in front of the element: a dialog, the bell's panel, the sign-in popup that does not
 * hold it. A bubble pointing past one at an element behind it would read as part of the dialog. An
 * element INSIDE the dialog on top is fine - that is where a new control in a dialog lives.
 */
const coveredByAnotherLayer = (element: HTMLElement) =>
  Array.from(document.querySelectorAll('[role="dialog"], [role="alertdialog"]')).some(
    (layer) => !layer.closest('[data-announcement-bubble]') && !layer.contains(element)
  );

/**
 * Picks the one bubble to show, the first time a reader meets a feature's new element (HAT-1489).
 *
 * A bubble walks through ONE screen: «הבא» moves to the next step and scrolls to it. When the next
 * step's element is not on this screen, the bubble offers «הבנתי» instead and ends there - pressing
 * «הבא» and seeing nothing happen read as broken, and a step turning up later on another screen read
 * as out of context. Using the element still moves on, so a step inside the dialog a button opens
 * appears once the user presses that button.
 *
 * One bubble at a time, oldest news first, and only while its element is in view. It stays until it
 * is answered - «הבנתי», «דלג», or using the element - and Escape puts it away until the next screen.
 * Finishing it marks the announcement read, in the bell too.
 *
 * Draws nothing itself: the anchor that owns the element draws the bubble (see AnnouncementAnchor).
 */
const AnnouncementTour = () => {
  const { pathname } = useLocation();
  const { currentUser } = useAuthContext();
  const announcements = useAnnouncements();
  const email = currentUser?.email;
  const [target, setTarget] = useState<Target | null>(null);
  const [snoozedOn, setSnoozedOn] = useState<string | null>(null);
  // Re-reads the stored step after a move, without re-reading localStorage on every look.
  const [progressVersion, setProgressVersion] = useState(0);

  const withSteps = useMemo(() => (announcements?.unread ?? []).filter((message) => message.tour.length > 0), [announcements?.unread]);

  const anchorsNamed = announcements?.anchorsNamed;
  const markRead = announcements?.markRead;
  const setBubble = announcements?.setBubble;

  const look = useCallback(() => {
    if (!anchorsNamed || withSteps.length === 0 || snoozedOn === pathname) {
      setTarget(null);
      return;
    }

    for (const message of withSteps) {
      const stepIndex = readProgress(email, message.id);
      if (stepIndex >= message.tour.length) continue;

      const anchorOf = (index: number) => message.tour[index].anchor || message.featureFlagKey || '';
      const reachable = (el: HTMLElement) => isShown(el) && !coveredByAnotherLayer(el);
      const element = anchorsNamed(anchorOf(stepIndex)).find((el) => reachable(el) && isInView(el));
      if (!element) continue;
      const nextHere = stepIndex + 1 < message.tour.length && anchorsNamed(anchorOf(stepIndex + 1)).some(reachable);

      setTarget((previous) =>
        previous?.message.id === message.id && previous.stepIndex === stepIndex && previous.element === element && previous.nextHere === nextHere
          ? previous
          : { message, stepIndex, element, nextHere }
      );
      return;
    }

    setTarget(null);
    // progressVersion: a move re-runs the look at once instead of waiting for the next tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorsNamed, withSteps, snoozedOn, pathname, email, progressVersion]);

  useEffect(() => {
    if (withSteps.length === 0) {
      setTarget(null);
      return undefined;
    }
    look();
    const timer = window.setInterval(look, LOOK_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [look, withSteps.length]);

  // A snooze lasts for the screen it was given on.
  useEffect(() => {
    if (snoozedOn && snoozedOn !== pathname) setSnoozedOn(null);
  }, [pathname, snoozedOn]);

  const finish = useCallback(
    (message: SystemMessageDtoIn) => {
      writeProgress(email, message.id, null);
      setTarget(null);
      void markRead?.([message.id]);
    },
    [email, markRead]
  );

  const advance = useCallback(() => {
    if (!target) return;
    const next = target.stepIndex + 1;
    if (next >= target.message.tour.length) {
      finish(target.message);
      return;
    }
    writeProgress(email, target.message.id, next);
    setTarget(null);
    setProgressVersion((version) => version + 1);

    // Take the reader to the next step: it may be further down the screen or the dialog.
    const nextElement = anchorsNamed?.(target.message.tour[next].anchor || target.message.featureFlagKey || '').find(isShown);
    if (nextElement && !isInView(nextElement)) {
      nextElement.scrollIntoView({ block: 'center', behavior: 'smooth' });
      window.setTimeout(() => setProgressVersion((version) => version + 1), 450);
    }
  }, [target, email, finish, anchorsNamed]);

  // Hand the chosen bubble to the anchor that draws it.
  useEffect(() => {
    setBubble?.(
      target
        ? { ...target, advance, finish: () => finish(target.message), snooze: () => setSnoozedOn(pathname) }
        : null
    );
  }, [target, advance, finish, pathname, setBubble]);

  /*
   * Using the element is the clearest «הבנתי» there is. Capture phase, so it is seen even when the
   * element stops the click from bubbling (the row actions do).
   */
  const advanceRef = useRef(advance);
  advanceRef.current = advance;
  useEffect(() => {
    if (!target) return undefined;
    const onUse = () => advanceRef.current();
    target.element.addEventListener('click', onUse, { capture: true, once: true });
    return () => target.element.removeEventListener('click', onUse, { capture: true });
  }, [target]);

  return null;
};

export default AnnouncementTour;
